/**
 * The measurement readouts: the floor-area / perimeter / wall-area figure
 * cards, and the flooring-by-type breakdown list, where each type's picker
 * renames it everywhere.
 */

import { css } from '@emotion/react';
import {
  floorTypeOfSceneType,
  formatDecimalFeet,
  formatInches,
  formatSquareFeet,
  MeasurementResult,
  replaceFloorTypeEverywhere,
  splitFeetInches,
  toHalfInches,
  useFloorPlanEditorState,
  useFloorPlanPreferences,
} from '@kaiizen/kai-floor-plan-editor-core';
import { fBody, fDisplay, ink, midtone, monoLabel } from '../../styles';
import { FIELD_ROW_STYLE, SECTION_STYLE } from './chrome';
import { ReadOnlyField } from './fields';
import { FloorTypePicker, useFloorTypeLabel } from './FloorTypeSelect';
import { usePanelReadOnly } from './panelReadOnly';

/** One row, three equal stat columns (floor / perimeter / wall). */
const FIGURES_GRID_STYLE = css`
  label: FIGURES_GRID_STYLE;
  display: flex; gap: 8px; margin-bottom: 18px;
`;

/** Bare stat column with no card chrome, maximizing the space for the values. */
const FIGURE_CARD_STYLE = css`
  label: FIGURE_CARD_STYLE;
  flex: 1; min-width: 0;
`;

const FIGURE_LABEL_STYLE = css`
  label: FIGURE_LABEL_STYLE;
  ${monoLabel}
  font-size: 9.5px; color: ${midtone}; letter-spacing: 0.14em;
`;

const FIGURE_VALUE_STYLE = css`
  label: FIGURE_VALUE_STYLE;
  font-family: ${fDisplay};
  font-size: 19px; font-weight: 700; letter-spacing: -0.015em;
  color: ${ink}; margin-top: 3px;
  small { font-family: ${fBody}; font-weight: 400; font-size: 12px; color: ${midtone}; }
`;

const U = 'ft';

function FigureCard({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div css={FIGURE_CARD_STYLE}>
      <div css={FIGURE_LABEL_STYLE}>{label}</div>
      <div css={FIGURE_VALUE_STYLE}>{children}</div>
    </div>
  );
}

/**
 * A length in the preferred units, its unit marks as the cards' small labels:
 * feet and inches (nearest half inch, a half as the ½ glyph; inches omitted
 * when zero), total inches, or decimal feet.
 */
function PerimeterFigure({ lengthFeet }: { lengthFeet: number }): React.JSX.Element {
  const { units } = useFloorPlanPreferences();
  if (units === 'inches') {
    return (
      <>
        {formatInches(toHalfInches(lengthFeet))} <small>in</small>
      </>
    );
  }
  if (units === 'feet') {
    return (
      <>
        {formatDecimalFeet(lengthFeet)} <small>{U}</small>
      </>
    );
  }
  const split = splitFeetInches(lengthFeet);
  return (
    <>
      {split.feet} <small>{U}</small>
      {split.inches > 0 && (
        <>
          {formatInches(split.inches)} <small>in</small>
        </>
      )}
    </>
  );
}

export function FigureCards({ m }: { m: MeasurementResult }): React.JSX.Element {
  // Areas display as whole square feet, rounded up; the perimeter follows the
  // units preference.
  return (
    <div css={FIGURES_GRID_STYLE}>
      <FigureCard label='Floor'>
        {formatSquareFeet(m.totalFloorArea)} <small>sq{U}</small>
      </FigureCard>
      <FigureCard label='Perimeter'>
        <PerimeterFigure lengthFeet={m.perimeter} />
      </FigureCard>
      <FigureCard label='Wall'>
        {formatSquareFeet(m.wallArea)} <small>sq{U}</small>
      </FigureCard>
    </div>
  );
}

/**
 * One flooring-by-type row: the type picker + its area. Re-picking a type
 * renames it EVERYWHERE (room default + every explicit flooring element:
 * replaceFloorTypeEverywhere), so picking a type already on another row
 * merges the two rows. The scene's unset bucket shows as unset, and
 * unpicking a type merges its row back into that bucket.
 */
function FlooringTypeRow({ type, area }: { type: string; area: number }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const floorTypeLabel = useFloorTypeLabel(floorTypeOfSceneType(type));
  if (usePanelReadOnly()) return <ReadOnlyField id={`flooring-${type}`} label={floorTypeLabel} value={`${formatSquareFeet(area)} sq${U}`} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <FloorTypePicker
        ariaLabel='Flooring type'
        value={floorTypeOfSceneType(type)}
        onCommit={toKey => {
          if (toKey !== type) replaceFloorTypeEverywhere(floorPlanEditorState, type, toKey);
        }}
      />
      <span>{formatSquareFeet(area)} sq{U}</span>
    </div>
  );
}

/** The flooring-by-type rows, largest area first, plus the unfloored line. */
export function FlooringSection({ m }: { m: MeasurementResult }): React.JSX.Element {
  return (
    <div css={SECTION_STYLE}>
      <h2>Flooring by type</h2>
      {m.areaByType
        // filter's fresh array makes the in-place sort safe
        .filter(areaByType => areaByType.area > 0.05)
        .sort((a, b) => b.area - a.area)
        .map(areaByType => <FlooringTypeRow key={areaByType.type} type={areaByType.type} area={areaByType.area} />)}
      {m.unflooredArea > 0.05 && (
        <div css={FIELD_ROW_STYLE}>
          <label>Unfloored (counters)</label>
          <span>{formatSquareFeet(m.unflooredArea)} sq{U}</span>
        </div>
      )}
    </div>
  );
}
